/**
 * VideoCard
 * Card displaying a single video with thumbnail and metadata
 */

import { makeStyles, tokens, Text, Badge, Card } from '@fluentui/react-components'
import { PlayCircleRegular, SparkleRegular, VideoRegular } from '@fluentui/react-icons'
import type { VideoInfo } from '../../../../domain'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    cursor: 'pointer',
    ':hover': {
      boxShadow: tokens.shadow4,
    },
  },
  thumbnail: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16/9',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
  },
  duration: {
    position: 'absolute',
    bottom: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase100,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  typeIcon: {
    fontSize: '14px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    marginTop: '2px',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  typeBadge: {
    alignSelf: 'flex-start',
  },
  summary: {
    color: tokens.colorNeutralForeground2,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
})

export interface VideoCardProps {
  video: VideoInfo
  onClick?: (video: VideoInfo) => void
}

export function VideoCard({ video, onClick }: VideoCardProps) {
  const styles = useStyles()

  const handleClick = () => {
    if (onClick) {
      onClick(video)
    } else {
      window.open(video.url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card className={styles.root} onClick={handleClick}>
      <div className={styles.thumbnail}>
        {video.thumbnailUrl ? (
          <img src={video.thumbnailUrl} alt={video.title} className={styles.thumbnailImage} />
        ) : (
          <PlayCircleRegular className={styles.playIcon} />
        )}
        <span className={styles.duration}>{video.duration}</span>
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          {video.type === 'ai_generated' ? (
            <SparkleRegular className={styles.typeIcon} />
          ) : (
            <VideoRegular className={styles.typeIcon} />
          )}
          <Text size={200} className={styles.title}>
            {video.title}
          </Text>
        </div>
        <Badge
          appearance="tint"
          color={video.type === 'ai_generated' ? 'brand' : 'informative'}
          size="small"
          className={styles.typeBadge}
        >
          {video.type === 'ai_generated' ? 'AI Generated' : 'User Created'}
        </Badge>
        {video.summary && (
          <Text size={200} className={styles.summary}>
            {video.summary}
          </Text>
        )}
      </div>
    </Card>
  )
}
